import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { Route as BaseRoute, Switch, Redirect } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import Load from './Loadable';
import { ModuleRepository } from '_iae/module/repository';
import { LoginForgot, ResetPassword, ViewStore } from '@code-yellow/spider';

// Use the sentry route to enable parameter discovery by react router before
// sending that information to sentry
const Route = Sentry.withSentryRouting(BaseRoute);

const UserOverview = Load(() => import('../screen/User/Overview'));
const UserEdit = Load(() => import('../screen/User/Edit'));
const GlobalValueOverview = Load(() => import('../screen/GlobalValue/Overview'));
const GlobalFileOverview = Load(() => import('../screen/GlobalFile/Overview'));
const GlobalFileEdit = Load(() => import('../screen/GlobalFile/Edit'));
const EmailLogOverview = Load(() => import('../screen/User/EmailLog'));

const AccountDetails = Load(() => import('../screen/AccountDetails'));

const EmailTemplateOverview = Load(() => import('../screen/EmailTemplates/Overview'));
const EmailTemplateEdit = Load(() => import('../screen/EmailTemplates/Edit'));


const Changelog = Load(() => import('../screen/Changelog'));


const NotFound = Load(() => import('../container/NotFound'));


const TicketOverview = Load(() => import('../screen/Ticket/Overview'));
const TagOverview = Load(() => import('../screen/Tag/Overview'));
const ProjectOverview = Load(() => import('screen/Project/Overview'));
const ProjectEditScreen = Load(() => import('../screen/Project/Edit'));
const ServerOverview = Load(() => import('../screen/Server/Overview'));

const TimeRegistrationOverview = Load(() => import('../screen/TimeRegistration/Overview'));
const TimeRegistrationMyTime = Load(() => import('../screen/TimeRegistration/MyTime'))
const MilestoneOverview = Load(() => import('../screen/Milestone/Overview'));


const OperationsScreen = Load(() => import('../screen/Operations/Screen'));
const UserScheduleScreen = Load(() => import('../screen/Operations/UserSchedule'));
const EntityOverviewScreen = Load(() => import('../screen/Entity/Overview'));
const CompanyOverviewScreen = Load(() => import('../screen/Company/Overview'));
const CompanyViewportScreen = Load(() => import('../screen/Company/Viewport'));
const ContactOverviewScreen = Load(() => import('../screen/Contact/Overview'));
const TeamOverviewScreen = Load(() => import('../screen/Team/Overview'));
const ContractOverviewScreen = Load(() => import('../screen/Contract/Overview'));
const ContractEditScreen = Load(() => import('../screen/Contract/Edit'));

const ModuleOverviewScreen = Load(() => import('screen/Module/Overview'));
const ModuleEditScreen = Load(() => import('screen/Module/Edit'))
const DocsScreen = Load(() => import('screen/Docs'))

const ProjectOverviewScreen = Load(() => import('../screen/Planning/ProjectOverview'));
const ProjectDetailOverviewScreen = Load(() => import('../screen/Planning/ProjectDetailOverview'));
const MilestoneDetailOverviewScreen = Load(() => import('../screen/Planning/MilestoneDetailOverview'));
const MilestoneStatusOverviewScreen = Load(() => import('../screen/Planning/MilestoneStatusOverview'));
const DeveloperOverviewScreen = Load(() => import('../screen/Planning/DeveloperOverview'));
const TimeRegistrationHistoryScreen = Load(() => import('../screen/Planning/TimeRegistrationHistory'));

const NoMfaScreen = Load(() => import('screen/ISO/NoMfa'))
const IsoDocumentScreen = Load(() => import('screen/ISO/Document/Document/Overview'));
const IsoChapterScreen = Load(() => import('screen/ISO/Document/Chapter/Overview'));

export default class Router extends Component {
    static propTypes = {
        store: PropTypes.instanceOf(ViewStore).isRequired,
        moduleRepository: PropTypes.instanceOf(ModuleRepository).isRequired
    };

    // react-router is a bit too verbose so I made a shorthand
    route = Screen => {
        const { moduleRepository } = this.props;
        return rProps => <Screen {...rProps} viewStore={this.props.store} moduleRepository={moduleRepository}/>;
    };

    redirectFromHome = () => {
        const { currentUser } = this.props.store;
        let uri = currentUser.isSuperuser ? (
            '/assets/user/overview'
        ) : (
            '/time-registration/my-time'
        );

        return <Redirect to={uri}/>;
    };

    render() {
        const { moduleRepository } = this.props;

        return (
            <Switch>
                <Route path="/" key="/" exact render={this.redirectFromHome}/>

                <Route path="/assets/user/overview" key="/assets/user/overview" render={this.route(UserOverview)}/>
                <Route path="/assets/user/:id/edit" key="/assets/user/:id/edit" render={this.route(UserEdit)}/>
                <Route path="/assets/user/add" key="/assets/user/add" render={this.route(UserEdit)}/>
                <Route path="/assets/user/email-log/overview" key="/assets/user/email-log/overview" render={this.route(EmailLogOverview)}/>

                <Route path="/assets/global-value/overview" key="/assets/global-value/overview" render={this.route(GlobalValueOverview)}/>
                {/* <Route path="/assets/global-value/:id/edit" key="/assets/global-value/:id/edit" render={this.route(GlobalValueEdit)}/> */}

                <Route path="/assets/global-file/overview" key="/assets/global-file/overview" render={this.route(GlobalFileOverview)}/>
                <Route path="/assets/global-file/:id/edit" key="/assets/global-file/:id/edit" render={this.route(GlobalFileEdit)}/>

                <Route path="/assets/email-template/overview" key="/assets/email-template/overview" render={this.route(EmailTemplateOverview)} />
                <Route path="/assets/email-template/add" key="/assets/email-template/add" render={this.route(EmailTemplateEdit)}/>
                <Route path="/assets/email-template/:id/edit" key="/assets/email-template/:id/edit" render={this.route(EmailTemplateEdit)}/>

                <Route path="/operations/ticket/overview" render={this.route(TicketOverview)} />
                <Route path="/operations/tag/overview" render={this.route(TagOverview)} />
                <Route path="/operations/milestone/overview" render={this.route(MilestoneOverview)}/>
                <Route path="/operations/project/overview" render={this.route(ProjectOverview)} />
                <Route path="/operations/project/add" render={this.route(ProjectEditScreen)} />
                <Route path="/operations/project/:id/edit" render={this.route(ProjectEditScreen) } />
                <Route path="/operations/operations/overview/:project_type" render={this.route(OperationsScreen)} />
                <Route path="/operations/user-schedule/overview" render={this.route(UserScheduleScreen)} />


                <Route path="/infra/server/overview" render={this.route(ServerOverview)} />

                <Route path="/crm/company/overview" render={this.route(CompanyOverviewScreen)} />
                <Route path="/crm/company/:id(\d+)" render={this.route(CompanyViewportScreen)} />
                <Route path="/crm/contact/overview" render={this.route(ContactOverviewScreen)} />
                <Route path="/crm/team/overview" render={this.route(TeamOverviewScreen)} />
                <Route path="/crm/entity/overview" render={this.route(EntityOverviewScreen)} />
                <Route path="/crm/contract/overview" render={this.route(ContractOverviewScreen)} />
                <Route path="/crm/contract/add" render={this.route(ContractEditScreen)} />
                <Route path="/crm/contract/:id/edit" render={this.route(ContractEditScreen) } />

                <Route path="/account/details" render={this.route(AccountDetails)}/>
                <Route path="/account/changelog" render={this.route(Changelog)}/>
                <Route path="/login/forgot" render={this.route(LoginForgot)}/>
                <Route path="/user/:id/reset-password/:token" render={this.route(ResetPassword)}/>

                <Route path="/time-registration/overview" render={this.route(TimeRegistrationOverview)}/>
                <Route path="/time-registration/my-time/:week?" render={this.route(TimeRegistrationMyTime)} />

                <Route path="/docs/" render={this.route(DocsScreen)} />

                <Route path="/planning/project/overview" key="/planning/project/overview" render={this.route(ProjectOverviewScreen)} />
                <Route path="/planning/project/:id/overview" key="/planning/project/:id/overview" render={this.route(ProjectDetailOverviewScreen)} />
                <Route path="/planning/milestone/:id/overview" key="/planning/milestone/:id/overview" render={this.route(MilestoneDetailOverviewScreen)} />
                <Route path="/planning/milestone/:id/status" key="/planning/milestone/:id/status" render={this.route(MilestoneStatusOverviewScreen)} />
                <Route path="/planning/developer/overview" key="/planning/developer/overview" render={this.route(DeveloperOverviewScreen)} />
                <Route path="/planning/time/history" key="/planning/time/history" render={this.route(TimeRegistrationHistoryScreen)} />

                <Route path="/roadmap/module/overview" render={this.route(ModuleOverviewScreen)} />
                <Route path="/roadmap/module/add" render={this.route(ModuleEditScreen)} />
                <Route path="/roadmap/module/:id/edit" render={this.route(ModuleEditScreen)} />

                <Route path="/iso/nomfa" render={this.route(NoMfaScreen)} />
                <Route path="/iso/document/document/overview" render={this.route(IsoDocumentScreen)} />
                <Route path="/iso/document/chapter/overview" render={this.route(IsoChapterScreen)} />

                {moduleRepository.getRoutes().map(moduleRoute => {
                    return <Route key={moduleRoute.path} path={moduleRoute.path} render={this.route(moduleRoute.screen)}/>
                })}

                <Route render={this.route(NotFound)}/>
            </Switch>
        );
    }
}